import { useState, useEffect } from 'react'

export const useNextLocalStorage = <T>(
  key: string,
  initialValue: T
): {
  value: T
  setValue: (value: T) => void
  removeValue: () => void
  isLoading: boolean
} => {
  // State to hold the current value
  const [value, setValue] = useState<T>(initialValue)
  // Loading state for SSR and initial hydration
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      try {
        // Get stored value
        const item = window.localStorage.getItem(key)
        // Parse stored json or return initialValue
        setValue(item ? JSON.parse(item) : initialValue)
      } catch (error) {
        // If error, return initialValue
        setValue(initialValue)
        console.error('Error reading from localStorage:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }, [key, initialValue])

  const updateValue = (newValue: T) => {
    try {
      // Save state
      setValue(newValue)
      // Save to localStorage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(newValue))
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }
  }

  const removeValue = () => {
    try {
      // Remove from localStorage
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key)
      }
      // Reset state to initial value
      setValue(initialValue)
    } catch (error) {
      console.error('Error removing from localStorage:', error)
    }
  }

  return {
    value,
    setValue: updateValue,
    removeValue,
    isLoading
  }
}
